import {
  collection,
  doc,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  serverTimestamp,
  Timestamp,
  type DocumentSnapshot,
} from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from './config';
import type { MatchJournal, PreMatchFormData, PostMatchFormData } from '@/types/matchJournal';
import type { Sport } from '@/types/sport';
import { replaceInsightHighlights } from './highlightService';
import { v4 as uuidv4 } from 'uuid';
import { NoteCountExceededError } from '@/types/noteCount';
import { getNoteLimit, fetchTotalNoteCount } from './noteCountService';

// テキスト配列をBulletItem配列に変換
function textsToBullets(texts: string[]) {
  return texts
    .filter((t) => t.trim().length > 0)
    .map((text) => ({ id: uuidv4(), text: text.trim(), isPinned: false }));
}

// 試合前ノート作成
// グループに参加している場合は集計クエリで上限チェックを行い、
// 上限を超えている場合は NoteCountExceededError をスローする。
export async function createPreMatchNote(
  userId: string,
  groupId: string | null,
  data: PreMatchFormData
): Promise<{ journalId: string }> {
  if (groupId) {
    // グループオーナーのプラン参照と総数集計で上限チェック
    const groupRef = doc(db, 'groups', groupId);
    const groupSnap = await getDoc(groupRef);
    if (!groupSnap.exists()) {
      throw new Error('GROUP_NOT_FOUND');
    }
    const ownerUid = (groupSnap.data().ownerUid ?? '') as string;
    const ownerSnap = await getDoc(doc(db, 'users', ownerUid));
    const plan = (ownerSnap.data()?.plan ?? 'free') as 'free' | 'paid';
    const purchasedCount = (ownerSnap.data()?.purchasedCount ?? 0) as number;
    const limit = getNoteLimit(plan, purchasedCount);

    const currentTotal = await fetchTotalNoteCount(groupId);
    if (currentTotal >= limit) {
      throw new NoteCountExceededError();
    }

    const journalRef = doc(collection(db, 'matchJournals'));
    await setDoc(journalRef, {
      userId,
      groupId,
      sport: data.sport,
      date: Timestamp.fromDate(new Date(data.date)),
      opponent: data.opponent,
      venue: data.venue,
      status: 'pre',
      isDraft: false,
      isPublic: data.isPublic,
      preNote: {
        goals: textsToBullets(data.goals),
        challenges: textsToBullets(data.challenges),
        recordedAt: serverTimestamp(),
      },
      postNote: null,
      reactionCounts: { applause: 0, fire: 0, star: 0, muscle: 0 },
      commentCount: 0,
      unreadCommentCount: 0,
      pinnedCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    return { journalId: journalRef.id };
  }

  // グループ未参加の場合（制限なし）
  const ref = await addDoc(collection(db, 'matchJournals'), {
    userId,
    groupId,
    sport: data.sport,
    date: Timestamp.fromDate(new Date(data.date)),
    opponent: data.opponent,
    venue: data.venue,
    status: 'pre',
    isDraft: false,
    isPublic: data.isPublic,
    preNote: {
      goals: textsToBullets(data.goals),
      challenges: textsToBullets(data.challenges),
      recordedAt: serverTimestamp(),
    },
    postNote: null,
    reactionCounts: { applause: 0, fire: 0, star: 0, muscle: 0 },
    commentCount: 0,
    unreadCommentCount: 0,
    pinnedCount: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return { journalId: ref.id };
}

// 試合後ノート追加（既存ジャーナルに追記）
export async function addPostMatchNote(
  journalId: string,
  userId: string,
  data: PostMatchFormData,
  imageUrls: string[] = []
): Promise<void> {
  const journalRef = doc(db, 'matchJournals', journalId);
  const snap = await getDoc(journalRef);
  if (!snap.exists() || snap.data()?.userId !== userId) {
    throw new Error('UNAUTHORIZED');
  }
  const journalData = snap.data() as MatchJournal;

  await updateDoc(journalRef, {
    status: 'completed',
    isPublic: data.isPublic,
    postNote: {
      result: data.result,
      myScore: data.myScore,
      opponentScore: data.opponentScore,
      goalReviews: data.goalReviews,
      achievements: textsToBullets(data.achievements),
      improvements: textsToBullets(data.improvements),
      explorations: textsToBullets(data.explorations),
      insights: textsToBullets(data.insights),
      performance: data.performance,
      imageUrls,
      recordedAt: serverTimestamp(),
    },
    updatedAt: serverTimestamp(),
  });

  // ハイライト同期は失敗しても振り返り保存は成功扱いにする
  try {
    await replaceInsightHighlights(
      userId, journalData.groupId ?? null, journalData.sport,
      'journal_insight', journalId, data.insights, journalData.date
    );
  } catch (e) {
    console.warn('[addPostMatchNote] replaceInsightHighlights failed (non-blocking):', e);
  }
}

// 試合前ノート更新
export async function updatePreMatchNote(
  journalId: string,
  userId: string,
  data: PreMatchFormData
): Promise<void> {
  const journalRef = doc(db, 'matchJournals', journalId);
  const snap = await getDoc(journalRef);
  if (!snap.exists() || snap.data()?.userId !== userId) {
    throw new Error('UNAUTHORIZED');
  }
  await updateDoc(journalRef, {
    sport: data.sport,
    date: Timestamp.fromDate(new Date(data.date)),
    opponent: data.opponent,
    venue: data.venue,
    isPublic: data.isPublic,
    preNote: {
      goals: textsToBullets(data.goals),
      challenges: textsToBullets(data.challenges),
      recordedAt: serverTimestamp(),
    },
    updatedAt: serverTimestamp(),
  });
}

// 試合後ノート更新
export async function updatePostMatchNote(
  journalId: string,
  userId: string,
  data: PostMatchFormData,
  imageUrls: string[] = []
): Promise<void> {
  const journalRef = doc(db, 'matchJournals', journalId);
  const snap = await getDoc(journalRef);
  if (!snap.exists() || snap.data()?.userId !== userId) {
    throw new Error('UNAUTHORIZED');
  }
  const journalData = snap.data() as MatchJournal;

  await updateDoc(journalRef, {
    isPublic: data.isPublic,
    postNote: {
      result: data.result,
      myScore: data.myScore,
      opponentScore: data.opponentScore,
      goalReviews: data.goalReviews,
      achievements: textsToBullets(data.achievements),
      improvements: textsToBullets(data.improvements),
      explorations: textsToBullets(data.explorations),
      insights: textsToBullets(data.insights),
      performance: data.performance,
      imageUrls,
      recordedAt: serverTimestamp(),
    },
    updatedAt: serverTimestamp(),
  });

  try {
    await replaceInsightHighlights(
      userId, journalData.groupId ?? null, journalData.sport,
      'journal_insight', journalId, data.insights, journalData.date
    );
  } catch (e) {
    console.warn('[updatePostMatchNote] replaceInsightHighlights failed (non-blocking):', e);
  }
}

// 試合後ノートのみ作成（試合前なし）
// グループに参加している場合は集計クエリで上限チェックを行う。
export async function createPostMatchOnly(
  userId: string,
  groupId: string | null,
  baseData: { sport: Sport; date: string; opponent: string; venue: string | null },
  postData: PostMatchFormData,
  imageUrls: string[] = []
): Promise<{ journalId: string }> {
  const journalData = {
    userId,
    groupId,
    sport: baseData.sport,
    date: Timestamp.fromDate(new Date(baseData.date)),
    opponent: baseData.opponent,
    venue: baseData.venue,
    status: 'post_only',
    isDraft: false,
    isPublic: postData.isPublic,
    preNote: null,
    postNote: {
      result: postData.result,
      myScore: postData.myScore,
      opponentScore: postData.opponentScore,
      goalReviews: [] as ReturnType<typeof textsToBullets>,
      achievements: textsToBullets(postData.achievements),
      improvements: textsToBullets(postData.improvements),
      explorations: textsToBullets(postData.explorations),
      insights: textsToBullets(postData.insights),
      performance: postData.performance,
      imageUrls,
      recordedAt: serverTimestamp(),
    },
    reactionCounts: { applause: 0, fire: 0, star: 0, muscle: 0 },
    commentCount: 0,
    unreadCommentCount: 0,
    pinnedCount: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  let journalId: string;

  if (groupId) {
    // 集計クエリで上限チェック
    const groupRef = doc(db, 'groups', groupId);
    const groupSnap = await getDoc(groupRef);
    if (!groupSnap.exists()) {
      throw new Error('GROUP_NOT_FOUND');
    }
    const ownerUid = (groupSnap.data().ownerUid ?? '') as string;
    const ownerSnap = await getDoc(doc(db, 'users', ownerUid));
    const plan = (ownerSnap.data()?.plan ?? 'free') as 'free' | 'paid';
    const purchasedCount = (ownerSnap.data()?.purchasedCount ?? 0) as number;
    const limit = getNoteLimit(plan, purchasedCount);

    const currentTotal = await fetchTotalNoteCount(groupId);
    if (currentTotal >= limit) {
      throw new NoteCountExceededError();
    }

    const journalRef = doc(collection(db, 'matchJournals'));
    await setDoc(journalRef, journalData);
    journalId = journalRef.id;
  } else {
    const ref = await addDoc(collection(db, 'matchJournals'), journalData);
    journalId = ref.id;
  }

  const sourceDate = Timestamp.fromDate(new Date(baseData.date));
  try {
    await replaceInsightHighlights(
      userId, groupId, baseData.sport,
      'journal_insight', journalId, postData.insights, sourceDate
    );
  } catch (e) {
    console.warn('[createPostMatchOnly] replaceInsightHighlights failed (non-blocking):', e);
  }
  return { journalId };
}

// ジャーナル削除（Storage画像も削除）
// 集計方式に変更したため、削除時に保存フィールドのデクリメントは不要。
// 残数表示は次回 fetchNoteCountInfo() の呼び出しで再集計される。
export async function deleteMatchJournal(journalId: string, userId: string): Promise<void> {
  const journalRef = doc(db, 'matchJournals', journalId);
  const snap = await getDoc(journalRef);
  if (!snap.exists() || snap.data()?.userId !== userId) {
    throw new Error('UNAUTHORIZED');
  }

  const journalData = snap.data() as MatchJournal;
  // Storage画像を削除
  if (journalData.postNote?.imageUrls) {
    for (const url of journalData.postNote.imageUrls) {
      try {
        // Firebase StorageのURLからパスを抽出して削除
        const storageRef = ref(storage, `matchJournals/${journalId}/${url.split('/').pop()?.split('?')[0]}`);
        await deleteObject(storageRef);
      } catch {
        // 画像削除失敗は無視（既に削除済みの場合など）
      }
    }
  }

  await deleteDoc(journalRef);
}

// ユーザーのジャーナル一覧（月別フィルター・クライアントソート）
export async function fetchUserJournals(
  userId: string,
  year: number,
  month: number
): Promise<MatchJournal[]> {
  const startDate = new Date(year, month - 1, 1);
  const endDate = new Date(year, month, 0, 23, 59, 59);
  const startTs = Timestamp.fromDate(startDate);
  const endTs = Timestamp.fromDate(endDate);

  const q = query(
    collection(db, 'matchJournals'),
    where('userId', '==', userId)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ ...(d.data() as MatchJournal), id: d.id }))
    .filter((j) => j.date.toMillis() >= startTs.toMillis() && j.date.toMillis() <= endTs.toMillis())
    .sort((a, b) => b.date.toMillis() - a.date.toMillis());
}

// ユーザーのジャーナル全件取得（クライアントソート）
export async function fetchUserJournalsPaged(
  userId: string,
  pageSize = 20,
  _lastDoc?: DocumentSnapshot
): Promise<{ journals: MatchJournal[]; lastDoc: DocumentSnapshot | null }> {
  const q = query(
    collection(db, 'matchJournals'),
    where('userId', '==', userId),
    limit(pageSize)
  );

  const snap = await getDocs(q);
  const journals = snap.docs
    .map((d) => ({ ...(d.data() as MatchJournal), id: d.id }))
    .sort((a, b) => b.date.toMillis() - a.date.toMillis());
  return { journals, lastDoc: null };
}

// グループのジャーナル一覧取得
export async function fetchGroupJournals(
  groupId: string,
  pageSize = 15,
  lastDoc?: DocumentSnapshot
): Promise<{ journals: MatchJournal[]; lastDoc: DocumentSnapshot | null }> {
  let q = query(
    collection(db, 'matchJournals'),
    where('groupId', '==', groupId),
    where('isPublic', '==', true),
    where('isDraft', '==', false),
    orderBy('date', 'desc'),
    limit(pageSize)
  );
  if (lastDoc) q = query(q, startAfter(lastDoc));

  const snap = await getDocs(q);
  const journals = snap.docs.map((d) => ({ ...(d.data() as MatchJournal), id: d.id }));
  return { journals, lastDoc: snap.docs[snap.docs.length - 1] ?? null };
}

// ジャーナル単件取得
export async function fetchJournal(journalId: string): Promise<MatchJournal | null> {
  const snap = await getDoc(doc(db, 'matchJournals', journalId));
  if (!snap.exists()) return null;
  return { ...(snap.data() as MatchJournal), id: snap.id };
}

// pinnedCountを更新（ピン操作時に呼ぶ）
export async function updateJournalPinnedCount(
  journalId: string,
  delta: number
): Promise<void> {
  const journalRef = doc(db, 'matchJournals', journalId);
  const snap = await getDoc(journalRef);
  if (!snap.exists()) return;
  const current = (snap.data() as MatchJournal).pinnedCount ?? 0;
  await updateDoc(journalRef, {
    pinnedCount: Math.max(0, current + delta),
    updatedAt: serverTimestamp(),
  });
}
